import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { roleApi, Permission, Module, Action, Role } from '@/services/roleApi';

interface EditRoleModalProps {
  role: Role;
  onClose: () => void;
  onSubmit: (id: string, data: {
    name?: string;
    description?: string;
    permissions?: Permission[];
    isActive?: boolean;
  }) => void;
  isLoading: boolean;
}

const EditRoleModal: React.FC<EditRoleModalProps> = ({
  role,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [formData, setFormData] = useState({
    name: role.name,
    description: role.description || '',
    isActive: role.isActive,
  });
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>(role.permissions);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Fetch permissions metadata
  const { data: permissionsData, isLoading: permissionsLoading } = useQuery({
    queryKey: ['permissions-meta'],
    queryFn: () => roleApi.getPermissionsMeta().then(res => res.data.data),
  });

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Role name must be less than 50 characters';
    }

    if (formData.description && formData.description.length > 200) {
      newErrors.description = 'Description must be less than 200 characters';
    }

    if (selectedPermissions.length === 0) {
      newErrors.permissions = 'At least one permission is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const updateData: any = {};
      
      // Only include changed fields
      if (formData.name !== role.name) {
        updateData.name = formData.name.trim();
      }
      if (formData.description !== (role.description || '')) {
        updateData.description = formData.description.trim() || undefined;
      }
      if (formData.isActive !== role.isActive) {
        updateData.isActive = formData.isActive;
      }
      
      // Check if permissions changed
      const permissionsChanged = JSON.stringify(selectedPermissions) !== JSON.stringify(role.permissions);
      if (permissionsChanged && !role.isSystem) {
        updateData.permissions = selectedPermissions;
      }

      onSubmit(role._id, updateData);
    }
  };

  const handlePermissionChange = (module: string, actions: string[]) => {
    setSelectedPermissions(prev => {
      const filtered = prev.filter(p => p.module !== module);
      if (actions.length > 0) {
        return [...filtered, { module, actions }];
      }
      return filtered;
    });
  };

  const getSelectedActions = (module: string): string[] => {
    const permission = selectedPermissions.find(p => p.module === module);
    return permission ? permission.actions : [];
  };

  const isActionSelected = (module: string, action: string): boolean => {
    const selectedActions = getSelectedActions(module);
    return selectedActions.includes(action);
  };

  const toggleAction = (module: string, action: string) => {
    if (role.isSystem) return; // Prevent editing system role permissions
    
    const currentActions = getSelectedActions(module);
    const newActions = currentActions.includes(action)
      ? currentActions.filter(a => a !== action)
      : [...currentActions, action];
    
    handlePermissionChange(module, newActions);
  };

  const selectAllActions = (module: string, allActions: string[]) => {
    if (role.isSystem) return;
    handlePermissionChange(module, allActions);
  };

  const deselectAllActions = (module: string) => {
    if (role.isSystem) return;
    handlePermissionChange(module, []);
  };

  // Quick role templates
  const applyRoleTemplate = (template: 'admin' | 'manager' | 'member' | 'viewer') => {
    if (role.isSystem) return;
    
    const templates = {
      admin: [
        { module: 'dashboard', actions: ['read'] },
        { module: 'organizations', actions: ['read'] },
        { module: 'projects', actions: ['create', 'read', 'update', 'delete', 'manage'] },
        { module: 'users', actions: ['create', 'read', 'update', 'invite'] },
        { module: 'roles', actions: ['read'] },
        { module: 'chat', actions: ['create', 'read', 'update', 'delete'] },
        { module: 'profile', actions: ['read', 'update'] },
        { module: 'settings', actions: ['read'] },
        { module: 'invitations', actions: ['create', 'read', 'update'] },
        { module: 'configurations', actions: ['read'] }
      ],
      manager: [
        { module: 'dashboard', actions: ['read'] },
        { module: 'organizations', actions: ['read'] },
        { module: 'projects', actions: ['create', 'read', 'update', 'manage'] },
        { module: 'users', actions: ['read', 'invite'] },
        { module: 'roles', actions: ['read'] },
        { module: 'chat', actions: ['create', 'read', 'update'] },
        { module: 'profile', actions: ['read', 'update'] },
        { module: 'invitations', actions: ['create', 'read'] }
      ],
      member: [
        { module: 'dashboard', actions: ['read'] },
        { module: 'organizations', actions: ['read'] },
        { module: 'projects', actions: ['read', 'update'] },
        { module: 'users', actions: ['read'] },
        { module: 'chat', actions: ['create', 'read', 'update'] },
        { module: 'profile', actions: ['read', 'update'] }
      ],
      viewer: [
        { module: 'dashboard', actions: ['read'] },
        { module: 'organizations', actions: ['read'] },
        { module: 'projects', actions: ['read'] },
        { module: 'users', actions: ['read'] },
        { module: 'chat', actions: ['read'] },
        { module: 'profile', actions: ['read', 'update'] }
      ]
    };

    setSelectedPermissions(templates[template]);
  };

  if (permissionsLoading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-medium text-gray-900">Edit Role</h3>
            {role.isSystem && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                System Role
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {role.isSystem && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">
                  System Role Restrictions
                </h3>
                <div className="mt-2 text-sm text-amber-700">
                  <p>
                    This is a system role. You can only modify the description and active status.
                    The name and permissions cannot be changed to maintain system integrity.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Role Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={role.isSystem}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                } ${role.isSystem ? 'bg-gray-50 text-gray-500' : ''}`}
                placeholder="Enter role name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                  errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter role description"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description}</p>
              )}
            </div>
          </div>

          {/* Active Status */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                Role is active
              </span>
            </label>
            <p className="mt-1 text-sm text-gray-500">
              Inactive roles cannot be assigned to new users
            </p>
          </div>

          {/* Quick Templates - Only for non-system roles */}
          {!role.isSystem && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quick Templates
              </label>
              <div className="flex flex-wrap gap-2">
                {(['admin', 'manager', 'member', 'viewer'] as const).map((template) => (
                  <button
                    key={template}
                    type="button"
                    onClick={() => applyRoleTemplate(template)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 capitalize"
                  >
                    {template}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setSelectedPermissions([])}
                  className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded-md hover:bg-red-50"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Permissions *
            </label>
            {errors.permissions && (
              <p className="mb-3 text-sm text-red-600">{errors.permissions}</p>
            )}
            
            <div className="space-y-4 max-h-96 overflow-y-auto border border-gray-200 rounded-md p-4">
              {permissionsData?.modules?.map((module: Module) => {
                const selectedActions = getSelectedActions(module.name);
                const allSelected = selectedActions.length === module.actions.length;
                const someSelected = selectedActions.length > 0 && selectedActions.length < module.actions.length;

                return (
                  <div key={module.name} className="border border-gray-200 rounded-md p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {module.label}
                        </h4>
                        <p className="text-xs text-gray-500">{module.description}</p>
                      </div>
                      {!role.isSystem && (
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => selectAllActions(module.name, module.actions)}
                            disabled={allSelected}
                            className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                          >
                            Select All
                          </button>
                          <button
                            type="button"
                            onClick={() => deselectAllActions(module.name)}
                            disabled={selectedActions.length === 0}
                            className="text-xs text-red-600 hover:text-red-800 disabled:text-gray-400"
                          >
                            Clear
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {module.actions.map((action: string) => {
                        const actionMeta = permissionsData?.actions?.find((a: Action) => a.name === action);
                        const selected = isActionSelected(module.name, action);
                        
                        return (
                          <label
                            key={action}
                            className={`flex items-center space-x-2 p-2 rounded border transition-colors ${
                              selected
                                ? 'bg-blue-50 border-blue-200'
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                            } ${role.isSystem ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleAction(module.name, action)}
                              disabled={role.isSystem}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 capitalize">
                                {actionMeta?.label || action}
                              </p>
                              {actionMeta?.description && (
                                <p className="text-xs text-gray-500 truncate">
                                  {actionMeta.description}
                                </p>
                              )}
                            </div>
                            {selected && (
                              <CheckIcon className="h-4 w-4 text-blue-600" />
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Updating...' : 'Update Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRoleModal;
